<template>
  <v-dialog
    v-model="dialog"
    max-width="600"
    scrollable
    content-class="guide-popup"
  >
    <v-card class="guide-card">
      <!-- ================= HERO ================= -->
      <div class="guide-hero">
        <span class="hero-bg hero-bg-1"></span>
        <span class="hero-bg hero-bg-2"></span>
        <span class="hero-bg hero-bg-3"></span>

        <v-btn icon dark fab small class="guide-close" @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>

        <div class="hero-icon-wrap">
          <v-icon class="hero-icon">{{ currentStep.icon }}</v-icon>
        </div>

        <div class="hero-step-chip">Step {{ step + 1 }} of {{ steps.length }}</div>

        <div class="guide-title">{{ currentStep.title }}</div>
        <div class="guide-sub">{{ currentStep.subtitle }}</div>

        <div class="guide-progress">
          <span
            v-for="(s, i) in steps"
            :key="i"
            class="progress-seg"
            :class="{ active: i <= step }"
          ></span>
        </div>
      </div>

      <!-- ================= BODY ================= -->
      <v-card-text class="guide-body">
        <v-slide-y-transition mode="out-in">
          <div :key="step">
            <!-- ═══════════ VENDEUR ═══════════ -->
            <template v-if="role === 'seller'">
              <div v-if="step === 0">
                <p class="guide-p">
                  This is where you manage and sell your products on the PettyCash Marketplace.
                </p>
                <div class="guide-tip">
                  <span class="tip-ico"><v-icon small>mdi-menu</v-icon></span>
                  <span>Use the tabs at the top: <b>Dashboard</b>, <b>Products</b> and <b>Add Product</b>.</span>
                </div>
                <p class="guide-p muted">
                  Before a sale succeeds, make sure your account balance covers the delivery and your products have enough stock.
                </p>
              </div>

              <div v-else-if="step === 1">
                <p class="guide-p">The Dashboard gives you a snapshot of your business:</p>
                <ul class="guide-list">
                  <li><span class="bullet indigo"><v-icon small>mdi-wallet</v-icon></span> <span>Your balance and amount earned</span></li>
                  <li><span class="bullet indigo"><v-icon small>mdi-package-variant</v-icon></span> <span>Your products and their stock</span></li>
                  <li><span class="bullet indigo"><v-icon small>mdi-cash</v-icon></span> <span>Your sales and PettyCash commissions</span></li>
                  <li><span class="bullet indigo"><v-icon small>mdi-history</v-icon></span> <span>Transaction history</span></li>
                </ul>
                <div class="guide-tip">
                  <span class="tip-ico success"><v-icon small>mdi-information-outline</v-icon></span>
                  <span>The PettyCash platform keeps a % commission on each sale; the rest is paid to you automatically.</span>
                </div>
              </div>

              <div v-else-if="step === 2">
                <p class="guide-p">In <b>Products</b> you see everything you currently sell:</p>
                <ul class="guide-list">
                  <li><span class="bullet indigo"><v-icon small>mdi-pencil</v-icon></span> <span>Edit a product (name, price, stock…)</span></li>
                  <li><span class="bullet indigo"><v-icon small>mdi-play-pause</v-icon></span> <span>Pause or resume a product</span></li>
                  <li><span class="bullet indigo"><v-icon small>mdi-chart-line</v-icon></span> <span>Follow how each product performs</span></li>
                </ul>
              </div>

              <div v-else-if="step === 3">
                <p class="guide-p"><b>Add Product</b> lets you list a new item:</p>
                <ul class="guide-list">
                  <li><span class="bullet indigo"><v-icon small>mdi-image-outline</v-icon></span> <span>A good picture helps a lot</span></li>
                  <li><span class="bullet indigo"><v-icon small>mdi-currency-cfa</v-icon></span> <span>Set your selling price and stock</span></li>
                  <li><span class="bullet indigo"><v-icon small>mdi-check-decagram</v-icon></span> <span>Once approved, it appears in the marketplace</span></li>
                </ul>
              </div>

              <div v-else-if="step === 4">
                <p class="guide-p">Approved <b>resellers</b> can also promote your products:</p>
                <ul class="guide-list">
                  <li><span class="bullet indigo"><v-icon small>mdi-share-variant</v-icon></span> <span>They share your product on WhatsApp / Facebook with their own link</span></li>
                  <li><span class="bullet indigo"><v-icon small>mdi-account-group</v-icon></span> <span>Every buyer that comes through them is a new customer for you</span></li>
                  <li><span class="bullet indigo"><v-icon small>mdi-cash-100</v-icon></span> <span>Their small reselling margin is taken from the sale — you keep the rest</span></li>
                </ul>
                <div class="guide-tip">
                  <span class="tip-ico success"><v-icon small>mdi-thumb-up-outline</v-icon></span>
                  <span>More visibility, more sales — without extra work on your side.</span>
                </div>
              </div>

              <div v-else-if="step === 5">
                <p class="guide-p">Example of a sale made through a reseller (real test):</p>
                <div class="money-card">
                  <div class="money-head">
                    <div>
                      <div class="money-head-label">Product at <b>15,000 FCFA</b> resold with +10% margin</div>
                      <div class="money-head-sub">sold through the reseller’s link</div>
                    </div>
                    <div class="money-head-price">16,500 FCFA</div>
                  </div>
                  <div class="money-row">
                    <span>PettyCash platform commission (−10%)</span>
                    <span class="neg">− 1,650</span>
                  </div>
                  <div class="money-row">
                    <span>Remainder</span>
                    <span class="strong">14,850 FCFA</span>
                  </div>
                  <div class="money-row">
                    <span>Reseller’s share (10% of the remainder)</span>
                    <span class="reseller">− 1,485</span>
                  </div>
                  <div class="money-foot">
                    <span><v-icon small color="success">mdi-arrow-down-bold</v-icon> Paid to you</span>
                    <strong class="success--text">13,365 FCFA</strong>
                  </div>
                </div>
                <div class="guide-tip">
                  <span class="tip-ico amber"><v-icon small>mdi-lightbulb-on-outline</v-icon></span>
                  <span>Sold directly (without a reseller): profit would be 15,000 − 1,500 commission = <b>13,500 FCFA</b>.</span>
                </div>
              </div>
            </template>

            <!-- ═══════════ REVENDEUR ═══════════ -->
            <template v-else-if="role === 'reseller'">
              <div v-if="step === 0">
                <p class="guide-p">
                  This is where you earn by reselling products from PettyCash sellers.
                </p>
                <div class="guide-tip">
                  <span class="tip-ico"><v-icon small>mdi-menu</v-icon></span>
                  <span>Two sections at the top: <b>Dashboard</b> and <b>Products</b>.</span>
                </div>
                <p class="guide-p muted">
                  You don’t need to stock anything: you only promote a product, and you earn a share of each sale made through your link.
                </p>
              </div>

              <div v-else-if="step === 1">
                <p class="guide-p">The Dashboard shows:</p>
                <ul class="guide-list">
                  <li><span class="bullet indigo"><v-icon small>mdi-wallet</v-icon></span> <span>Your balance and total earnings</span></li>
                  <li><span class="bullet indigo"><v-icon small>mdi-package-variant</v-icon></span> <span>The products you currently resell</span></li>
                  <li><span class="bullet indigo"><v-icon small>mdi-history</v-icon></span> <span>Your resale and transfer history</span></li>
                </ul>
                <div class="guide-tip">
                  <span class="tip-ico success"><v-icon small>mdi-bank-transfer</v-icon></span>
                  <span>Transfer your balance to your PettyCash wallet or to a savings campaign whenever you want.</span>
                </div>
              </div>

              <div v-else-if="step === 2">
                <p class="guide-p">In <b>Products</b>, the catalog lists all products available to resell.</p>
                <ul class="guide-list">
                  <li><span class="bullet indigo"><v-icon small>mdi-magnify</v-icon></span> <span>Use the search field to find a product</span></li>
                  <li><span class="bullet indigo"><v-icon small>mdi-plus-circle-outline</v-icon></span> <span>Click <b>Resell</b> to add it to your catalog</span></li>
                  <li><span class="bullet indigo"><v-icon small>mdi-check-circle-outline</v-icon></span> <span>It now appears in <b>My Catalog</b></span></li>
                </ul>
              </div>

              <div v-else-if="step === 3">
                <p class="guide-p">Once a product is in <b>My Catalog</b>, sharing it is one click away:</p>
                <div class="share-demo">
                  <span class="demo-pill wa"><i class="fab fa-whatsapp"></i> WhatsApp</span>
                  <span class="demo-pill fb"><i class="fab fa-facebook-f"></i> Facebook</span>
                  <span class="demo-pill cp"><i class="fas fa-link"></i> Copy link</span>
                </div>
                <div class="guide-tip">
                  <span class="tip-ico indigo"><v-icon small>mdi-source-branch</v-icon></span>
                  <span>Your <b>personal link</b> carries your attribution: every buyer who clicks it is tracked to you automatically.</span>
                </div>
                <p class="guide-p muted">The shared message includes the product image, the price, and your link (the “Copy link” button copies it directly).</p>
              </div>

              <div v-else-if="step === 4">
                <p class="guide-p">When a customer buys through your link, you earn a share of each sale.</p>
                <ul class="guide-list">
                  <li><span class="bullet indigo"><v-icon small>mdi-sale</v-icon></span> <span>The buyer pays the resale price (product price + your margin)</span></li>
                  <li><span class="bullet indigo"><v-icon small>mdi-percent-outline</v-icon></span> <span>The PettyCash commission is taken first</span></li>
                  <li><span class="bullet indigo"><v-icon small>mdi-cash-100</v-icon></span> <span>Your margin share is credited to your balance automatically</span></li>
                </ul>
              </div>

              <div v-else-if="step === 5">
                <p class="guide-p">Example of a resale through your link (real test):</p>
                <div class="money-card">
                  <div class="money-head">
                    <div>
                      <div class="money-head-label">Product at <b>15,000 FCFA</b> resold with +10% margin</div>
                      <div class="money-head-sub">your sale at the resale price</div>
                    </div>
                    <div class="money-head-price">16,500 FCFA</div>
                  </div>
                  <div class="money-row">
                    <span>PettyCash platform commission (−10%)</span>
                    <span class="neg">− 1,650</span>
                  </div>
                  <div class="money-row">
                    <span>Remainder</span>
                    <span class="strong">14,850 FCFA</span>
                  </div>
                  <div class="money-row">
                    <span>Your share (10% of the remainder)</span>
                    <span class="reseller">+ 1,485</span>
                  </div>
                  <div class="money-foot">
                    <span><v-icon small color="success">mdi-cash-check</v-icon> Credited to your balance</span>
                    <strong class="success--text">+ 1,485 FCFA</strong>
                  </div>
                </div>
                <div class="guide-tip">
                  <span class="tip-ico amber"><v-icon small>mdi-lightbulb-on-outline</v-icon></span>
                  <span>The seller receives the balance: <b>13,365 FCFA</b>. Everything is automatic and visible in your history.</span>
                </div>
              </div>
            </template>
          </div>
        </v-slide-y-transition>
      </v-card-text>

      <!-- ================= FOOTER ================= -->
      <v-card-actions class="guide-actions">
        <v-btn text small color="grey darken-1" class="guide-skip" @click="dialog = false">
          {{ step === steps.length - 1 ? 'Close' : 'Skip' }}
        </v-btn>
        <v-spacer />
        <v-btn text rounded color="grey darken-1" :disabled="step === 0" @click="step--">
          <v-icon left small>mdi-chevron-left</v-icon> Back
        </v-btn>
        <v-btn color="primary" rounded dark @click="next">
          {{ step === steps.length - 1 ? 'Got it!' : 'Next' }}
          <v-icon right small>mdi-chevron-right</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'GuidePopup',

  props: {
    role: {
      type: String,
      default: 'reseller',
      validator: v => ['seller', 'reseller'].includes(v),
    },
    autoShow: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      dialog: false,
      step: 0,
    }
  },

  computed: {
    storageKey() {
      return `pc_guide_${this.role}_v1`
    },
    steps() {
      if (this.role === 'seller') {
        return [
          { icon: 'mdi-store-outline', title: 'Welcome to your Seller Space', subtitle: 'Sell your products on the PettyCash Marketplace' },
          { icon: 'mdi-view-dashboard', title: 'The Dashboard', subtitle: 'The health of your business at a glance' },
          { icon: 'mdi-package-variant', title: 'Your Products', subtitle: 'Manage what you already sell' },
          { icon: 'mdi-plus-circle-outline', title: 'Add a Product', subtitle: 'List a new item in a few clicks' },
          { icon: 'mdi-handshake-outline', title: 'Resellers boost your sales', subtitle: 'Other members promote your products' },
          { icon: 'mdi-cash-multiple', title: 'How you get paid', subtitle: 'A concrete example with real figures' },
        ]
      }
      return [
        { icon: 'mdi-storefront', title: 'Welcome to your Reseller Space', subtitle: 'Earn by promoting PettyCash products' },
        { icon: 'mdi-view-dashboard', title: 'The Dashboard', subtitle: 'Your balance, products and history' },
        { icon: 'mdi-cart-plus', title: 'Add a product to your catalog', subtitle: 'Pick what you want to resell' },
        { icon: 'mdi-share-variant', title: 'Share your products', subtitle: 'WhatsApp, Facebook or copy the link' },
        { icon: 'mdi-cash-100', title: 'How you earn', subtitle: 'You are paid a share of each sale' },
        { icon: 'mdi-cash-multiple', title: 'A real example', subtitle: 'From an actual tested resale' },
      ]
    },
    currentStep() {
      return this.steps[this.step] || this.steps[0]
    },
  },

  watch: {
    dialog(v) {
      if (!v) {
        try {
          localStorage.setItem(this.storageKey, '1')
        } catch (e) { /* ignore */ }
      } else if (this.$translator && typeof this.$translator.refreshIfEnabled === 'function') {
        this.$translator.refreshIfEnabled('dom', this.$el)
      }
    },
    autoShow(v) {
      if (v) this.maybeOpen()
    },
  },

  mounted() {
    this.maybeOpen()
  },

  methods: {
    maybeOpen() {
      if (!this.autoShow || this.dialog) return
      let seen = false
      try {
        seen = localStorage.getItem(this.storageKey) === '1'
      } catch (e) { /* ignore */ }
      if (!seen) {
        this.$nextTick(() => this.open())
      }
    },
    open() {
      this.step = 0
      this.dialog = true
    },
    next() {
      if (this.step < this.steps.length - 1) {
        this.step += 1
      } else {
        this.dialog = false
      }
    },
  },
}
</script>

<style scoped>
.guide-popup {
  border-radius: 18px !important;
  overflow: hidden;
  box-shadow: 0 24px 70px rgba(24, 33, 94, 0.28) !important;
}

/* ─────────── HERO ─────────── */
.guide-hero {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #4650b8 0%, #6a5acd 45%, #8e5bd0 100%);
  color: white;
  padding: 30px 24px 20px;
  text-align: center;
}

.hero-bg {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  pointer-events: none;
}

.hero-bg-1 { width: 200px; height: 200px; top: -70px; left: -60px; }
.hero-bg-2 { width: 120px; height: 120px; bottom: -40px; right: -20px; background: rgba(255,255,255,0.06); }
.hero-bg-3 { width: 70px; height: 70px; top: 18px; right: 70px; background: rgba(255,255,255,0.07); }

.guide-close {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(255, 255, 255, 0.22) !important;
  transition: background 0.2s ease;
  z-index: 2;
}

.guide-close:hover {
  background: rgba(255, 255, 255, 0.35) !important;
}

.hero-icon-wrap {
  width: 78px;
  height: 78px;
  margin: 0 auto 12px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-icon {
  color: white !important;
  font-size: 38px !important;
  text-shadow: 0 3px 12px rgba(0, 0, 0, 0.18);
}

.hero-step-chip {
  position: absolute;
  top: 14px;
  left: 14px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.guide-title {
  font-size: 21px;
  font-weight: 800;
  letter-spacing: 0.2px;
  margin-top: 2px;
}

.guide-sub {
  font-size: 13px;
  opacity: 0.92;
  margin-top: 3px;
}

.guide-progress {
  display: flex;
  gap: 5px;
  justify-content: center;
  margin-top: 16px;
}

.progress-seg {
  width: 40px;
  height: 5px;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.28);
  transition: background 0.3s ease, transform 0.3s ease;
}

.progress-seg.active {
  background: #ffffff;
  transform: scaleY(1.25);
}

/* ─────────── BODY ─────────── */
.guide-body {
  font-size: 14px;
  line-height: 1.65;
  color: #3a3f4d;
  padding: 18px 22px 8px;
}

.guide-p {
  margin: 0 0 10px;
}

.guide-p.muted {
  color: #7a8194;
  font-size: 13px;
}

.guide-list {
  list-style: none;
  padding: 0;
  margin: 8px 0 12px;
}

.guide-list li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
}

.bullet {
  width: 30px;
  height: 30px;
  min-width: 30px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 15px;
}

.bullet.indigo { background: linear-gradient(135deg, #5c6bc0, #7986cb); }
.bullet.success { background: linear-gradient(135deg, #26a69a, #4db6ac); }

.guide-tip {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #f4f6ff;
  border: 1px solid #e4e8ff;
  border-radius: 12px;
  padding: 10px 12px;
  margin-top: 12px;
  font-size: 13px;
}

.tip-ico {
  width: 26px;
  height: 26px;
  min-width: 26px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e8ecff;
  color: #4650b8;
}

.tip-ico.success { background: #e0f6f0; color: #00897b; }
.tip-ico.amber { background: #fff3e0; color: #ef8c00; }
.tip-ico.indigo { background: #e8ecff; color: #4650b8; }

/* ─────────── SHARE DEMO ─────────── */
.share-demo {
  display: flex;
  gap: 8px;
  margin: 12px 0;
  flex-wrap: wrap;
}

.demo-pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border-radius: 22px;
  padding: 6px 14px;
  font-size: 12.5px;
  font-weight: 600;
  color: white;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
}

.demo-pill.wa { background: #25d366; }
.demo-pill.fb { background: #1877f2; }
.demo-pill.cp { background: #64748b; }

/* ─────────── MONEY FLOW ─────────── */
.money-card {
  border: 1px solid #e6e9f2;
  border-radius: 14px;
  overflow: hidden;
  margin: 12px 0;
  background: #fff;
}

.money-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: linear-gradient(135deg, #f2f5ff, #f8faff);
  border-bottom: 1px dashed #e3e7f3;
}

.money-head-label {
  font-size: 13px;
  color: #3a3f4d;
}

.money-head-sub {
  font-size: 11.5px;
  color: #8a90a3;
  margin-top: 2px;
}

.money-head-price {
  font-size: 17px;
  font-weight: 800;
  color: #4650b8;
  white-space: nowrap;
}

.money-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  font-size: 13px;
  border-bottom: 1px dashed #f0f2f8;
}

.money-row .neg { color: #ef5350; font-weight: 600; }
.money-row .reseller { color: #1e88e5; font-weight: 700; }
.money-row .strong { font-weight: 700; color: #3a3f4d; }

.money-foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: #eafaf1;
  border-top: 1px solid #d7f0e2;
  font-size: 13.5px;
}

.money-foot strong {
  font-size: 16px;
}

/* ─────────── ACTIONS ─────────── */
.guide-actions {
  padding: 6px 18px 16px;
}

.guide-skip {
  text-transform: none;
  letter-spacing: 0.2px;
}
</style>